import { z } from 'zod';
import { AVATAR_DATA_URL_MAX, DISPLAY_NAME_MAX, PASSWORD_MAX } from '../constants.js';
import { idSchema, isoTimestampSchema } from './primitives.js';
/**
 * A user account as it appears on the wire.
 *
 * SECURITY RULE: this shape carries no password, no password hash, no salt and
 * no session token. Credentials live only inside the server's auth layer and
 * its `users` table; they never enter a shared DTO. If a repository returns a
 * row containing a hash, strip it before it becomes a `User`.
 */
/**
 * Where an account's identity lives.
 *
 * `local` accounts sign in with a password Orbit Sync holds. `ldap` accounts
 * belong to the organisation's directory (Active Directory or another LDAP
 * server): the directory checks the password and owns the name and email, so
 * neither can be changed here.
 */
export const authSourceSchema = z.enum(['local', 'ldap']);
export const userSchema = z.object({
    id: idSchema,
    email: z.email().max(254),
    displayName: z.string().trim().min(1).max(DISPLAY_NAME_MAX),
    createdAt: isoTimestampSchema,
    /**
     * When the profile picture last changed; `null` when there is none. Doubles as
     * the cache-buster in the picture's URL - see `avatarPath`.
     */
    avatarUpdatedAt: isoTimestampSchema.nullable(),
    authSource: authSourceSchema,
});
/**
 * Fields a client may supply when registering.
 *
 * The password is validated here because it must be checked at the HTTP
 * boundary, but it is deliberately absent from every response shape - see
 * `AuthProvider` on the server, which consumes this and returns a principal.
 *
 * Only the HARD bounds live in this schema. The password policy and the display
 * name limit are administrator settings, applied on top by the server with
 * `passwordPolicyIssues` - see `domain/settings.ts`.
 */
export const registerUserInputSchema = z.object({
    email: z.email().max(254),
    displayName: z.string().trim().min(1).max(DISPLAY_NAME_MAX),
    password: z.string().min(1).max(PASSWORD_MAX),
});
/**
 * Sign-in credentials.
 *
 * `email` is the account's email address - or, when directory sign-in is
 * enabled, a directory user name (`jdoe`, `CORP\jdoe` or `jdoe@corp.example`).
 * It is checked only for length, never for email syntax, so both kinds fit
 * one field; every request that was valid before directory sign-in still is.
 */
export const loginInputSchema = z.object({
    email: z.string().trim().min(1).max(254),
    password: z.string().min(1).max(PASSWORD_MAX),
});
/**
 * A user as everyone else sees them.
 *
 * The directory endpoints (`GET /api/v1/users`, `GET /api/v1/users/lookup`) are
 * readable by any signed-in account, so they deliberately hand back less than
 * `User`: an id to address someone by and a name to render. **No email.** A full
 * `User` would turn a people-picker into a company address-book export - a
 * caller could walk the alphabet and harvest every address.
 *
 * A user still sees their own email, via `GET /api/v1/auth/me`.
 */
export const publicUserSchema = userSchema.pick({
    id: true,
    displayName: true,
    avatarUpdatedAt: true,
});
/**
 * `PATCH /api/v1/auth/me`: change your own name or email.
 *
 * Changing the email needs the current password - it is the sign-in identity,
 * so a borrowed unlocked session must not be enough to take the account over.
 */
export const updateAccountInputSchema = z
    .object({
    displayName: z.string().trim().min(1).max(DISPLAY_NAME_MAX).optional(),
    email: z.email().max(254).optional(),
    currentPassword: z.string().min(1).max(PASSWORD_MAX).optional(),
})
    .refine((input) => input.displayName !== undefined || input.email !== undefined, {
    message: 'Nothing to change',
});
/** `POST /api/v1/auth/password`. The server applies the password policy on top. */
export const changePasswordInputSchema = z.object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX),
    newPassword: z.string().min(1).max(PASSWORD_MAX),
});
/**
 * `PUT /api/v1/auth/me/avatar`: a small, already-resized image as a data URL.
 * The browser crops and scales it, so the server only bounds and checks it.
 */
export const avatarInputSchema = z.object({
    dataUrl: z
        .string()
        .max(AVATAR_DATA_URL_MAX)
        .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/, 'Not a supported image'),
});
/** Where a user's picture is served from. The timestamp makes it cacheable forever. */
export function avatarPath(user) {
    return user.avatarUpdatedAt
        ? `/users/${user.id}/avatar?v=${encodeURIComponent(user.avatarUpdatedAt)}`
        : null;
}
//# sourceMappingURL=user.js.map